import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowLeft } from "lucide-react";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import { useSEO } from "@/lib/seo";

const LOGOS = {
  badge: "https://static.prod-images.emergentagent.com/jobs/258f7903-61e4-4ba1-a674-bd920a237754/images/47ff191a45beb5af56f2ab3f37af00fad971aaf57b317f6ce95865af864d3277.jpeg",
  icon: "https://static.prod-images.emergentagent.com/jobs/258f7903-61e4-4ba1-a674-bd920a237754/images/3928d3ad2e313636a540cff157a496f893fd8973494e1532a0e68826e55ad45c.jpeg",
  wordmark: "https://static.prod-images.emergentagent.com/jobs/258f7903-61e4-4ba1-a674-bd920a237754/images/f54400d30e72587fe632976b1a95b3f397dabe4d7ac646f404813e921f96933d.jpeg",
};

const palette = [
  { name: "Obsidian", hex: "#060606", note: "Primary background", dark: true },
  { name: "Volt Green", hex: "#CCFF00", note: "Primary accent / CTAs" },
  { name: "Hot Pink", hex: "#FF0055", note: "Secondary accent" },
  { name: "Surface", hex: "#121212", note: "Cards & panels", dark: true },
  { name: "White", hex: "#FFFFFF", note: "Text on dark" },
];

const BrandKit = () => {
  const navigate = useNavigate();
  useSEO({
    title: "Brand Kit | BrandToBuilt",
    description: "The BrandToBuilt brand kit — logo, colour palette, typography and voice guidelines.",
    path: "/brand",
  });

  return (
    <div className="pt-28 md:pt-36 pb-24 min-h-screen" data-testid="brand-kit">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#ccff00] transition-colors mb-8" data-testid="brand-back">
          <ArrowLeft size={16} /> Back to site
        </button>

        <motion.div variants={fadeUp} initial="hidden" animate="show" className="mb-16 max-w-3xl">
          <p className="text-[#ccff00] text-xs font-semibold uppercase tracking-widest mb-4">Brand kit</p>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-none mb-6 break-words">
            The BrandToBuilt look.
          </h1>
          <p className="text-lg text-zinc-400">
            Everything that makes the brand feel bold, fast and premium — logo, colours, type and voice. Download the assets and keep it consistent everywhere.
          </p>
        </motion.div>

        {/* Logos */}
        <Section title="01 — Logo">
          <div className="grid md:grid-cols-3 gap-6">
            <LogoCard img={LOGOS.badge} label="Primary lockup" bg="#060606" />
            <LogoCard img={LOGOS.icon} label="Icon / app mark" bg="#0d0d0d" contain />
            <LogoCard img={LOGOS.wordmark} label="Wordmark" bg="#060606" />
          </div>
        </Section>

        {/* Colours */}
        <Section title="02 — Colour palette">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {palette.map((c) => (
              <motion.div key={c.hex} variants={fadeUp} className="rounded-2xl overflow-hidden border border-white/10" data-testid={`swatch-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="h-28" style={{ background: c.hex, borderBottom: c.dark ? "1px solid rgba(255,255,255,0.08)" : "none" }} />
                <div className="p-4 bg-[#121212]">
                  <p className="font-display font-bold">{c.name}</p>
                  <p className="text-sm text-zinc-500 uppercase tracking-wide">{c.hex}</p>
                  <p className="text-xs text-zinc-600 mt-1">{c.note}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </Section>

        {/* Typography */}
        <Section title="03 — Typography">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="min-w-0 rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-8">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Display · Headings</p>
              <p className="font-display font-black text-4xl md:text-5xl mb-3 truncate">Unbounded</p>
              <p className="font-display text-2xl text-zinc-300">Aa Bb Cc 1234</p>
              <p className="text-sm text-zinc-500 mt-4">Weights 700–900. Tight tracking. Used for every headline.</p>
            </div>
            <div className="min-w-0 rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-8">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Body · UI</p>
              <p className="text-4xl md:text-5xl mb-3 truncate" style={{ fontFamily: "Outfit" }}>Outfit</p>
              <p className="text-2xl text-zinc-300" style={{ fontFamily: "Outfit" }}>Aa Bb Cc 1234</p>
              <p className="text-sm text-zinc-500 mt-4">Weights 300–600. Clean and friendly for all body copy.</p>
            </div>
          </div>
        </Section>

        {/* Voice */}
        <Section title="04 — Voice & tagline">
          <div className="rounded-2xl bg-[#ccff00] text-black p-8 md:p-12">
            <p className="font-display font-black text-3xl md:text-5xl leading-tight mb-4">Fast. Professional. Done for you.</p>
            <p className="text-black/70 text-lg max-w-2xl">Confident, plain-English and reassuring — never techy or salesy. Lead with the outcome ("your business online by Friday"), speak to real trades and startups, and always sound like a helpful human who's got it handled.</p>
          </div>
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="mb-16">
    <h2 className="font-display font-bold text-xl text-zinc-400 mb-6">{title}</h2>
    {children}
  </motion.section>
);

const LogoCard = ({ img, label, bg, contain }) => (
  <div className="rounded-2xl border border-white/10 overflow-hidden">
    <div className="h-52 flex items-center justify-center p-6" style={{ background: bg }}>
      <img src={img} alt={label} className={`max-h-full max-w-full ${contain ? "object-contain" : "object-contain"}`} />
    </div>
    <div className="flex items-center justify-between p-4 bg-[#121212]">
      <span className="text-sm text-zinc-300">{label}</span>
      <a href={img} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center gap-1.5 text-xs text-[#ccff00] hover:underline" data-testid={`download-${label.split(" ")[0].toLowerCase()}`}>
        <Download size={14} /> Download
      </a>
    </div>
  </div>
);

export default BrandKit;
